export type BioKey = 'preg_crl' | 'preg_bpd' | 'preg_hc' | 'preg_ac' | 'preg_fl';
export type BioType = 'CRL' | 'BPD' | 'HC' | 'AC' | 'FL';

export interface BioLimit {
  min: number;
  max: number;
  unit: 'mm' | 'cm';
}

export const BIO_LIMITS: Record<BioType, BioLimit> = {
  CRL: { min: 5, max: 95, unit: 'mm' },
  BPD: { min: 2.0, max: 12.0, unit: 'cm' },
  HC: { min: 10.0, max: 40.0, unit: 'cm' },
  AC: { min: 10.0, max: 45.0, unit: 'cm' },
  FL: { min: 1.0, max: 9.0, unit: 'cm' },
};

export const BIO_KEY_TO_TYPE: Record<BioKey, BioType> = {
  preg_crl: 'CRL',
  preg_bpd: 'BPD',
  preg_hc: 'HC',
  preg_ac: 'AC',
  preg_fl: 'FL',
};

export const BIO_TYPE_TO_KEY: Record<BioType, BioKey> = {
  CRL: 'preg_crl',
  BPD: 'preg_bpd',
  HC: 'preg_hc',
  AC: 'preg_ac',
  FL: 'preg_fl',
};

export interface GAResult {
  totalDays: number;
  weeks: number;
  days: number;
  error?: 'invalid' | 'out_of_range';
}

export const calculateGAFromBio = (
  type: BioType,
  value: number | string | null
): GAResult | null => {
  if (value === null || value === '' || value === undefined) return null;
  const val =
    typeof value === 'number' ? value : parseFloat(String(value).trim());
  if (Number.isNaN(val)) return null;

  const limit = BIO_LIMITS[type];
  if (val < limit.min || val > limit.max) {
    return { totalDays: 0, weeks: 0, days: 0, error: 'invalid' };
  }

  let gaWeeks = 0;
  if (type === 'BPD') {
    gaWeeks = 9.54 + 1.482 * val + 0.1676 * val * val;
  } else if (type === 'HC') {
    gaWeeks = 8.96 + 0.54 * val + 0.0003 * Math.pow(val, 3);
  } else if (type === 'AC') {
    gaWeeks = 8.14 + 0.753 * val + 0.0036 * val * val;
  } else if (type === 'FL') {
    gaWeeks = 10.35 + 2.46 * val + 0.17 * val * val;
  } else {
    // CRL: Robinson's log-cubic formula expects centimetres.
    const cm = val / 10.0;
    const exponent =
      1.684969 +
      0.315646 * cm -
      0.049306 * Math.pow(cm, 2) +
      0.004057 * Math.pow(cm, 3) -
      0.000120456 * Math.pow(cm, 4);
    gaWeeks = Math.exp(exponent);
  }

  const totalDays = gaWeeks * 7;
  const w = Math.floor(totalDays / 7);
  const d = Math.floor(totalDays % 7);

  if (w < 4 || w > 44) {
    return { totalDays: 0, weeks: 0, days: 0, error: 'out_of_range' };
  }
  return { totalDays, weeks: w, days: d };
};

export type BiometryValues = Partial<Record<BioKey, number | null>>;

export const deriveTotalDaysFromBiometry = (
  values: BiometryValues,
  preferCrl: boolean
): number | null => {
  if (preferCrl && values.preg_crl !== undefined && values.preg_crl !== null) {
    const res = calculateGAFromBio('CRL', values.preg_crl);
    if (res && !res.error && res.totalDays > 0) return res.totalDays;
  }

  let sum = 0;
  let count = 0;
  (['BPD', 'HC', 'AC', 'FL'] as BioType[]).forEach((type) => {
    const key = BIO_TYPE_TO_KEY[type];
    const v = values[key];
    if (v === undefined || v === null) return;
    const res = calculateGAFromBio(type, v);
    if (res && !res.error && res.totalDays > 0) {
      sum += res.totalDays;
      count += 1;
    }
  });
  if (count > 0) return sum / count;

  if (values.preg_crl !== undefined && values.preg_crl !== null) {
    const res = calculateGAFromBio('CRL', values.preg_crl);
    if (res && !res.error && res.totalDays > 0) return res.totalDays;
  }
  return null;
};

export const hasAnyBiometry = (values: BiometryValues): boolean =>
  (Object.keys(BIO_KEY_TO_TYPE) as BioKey[]).some(
    (k) => values[k] !== undefined && values[k] !== null
  );
